import { Sites } from '@ps/platform-message-framework/common/Sites';
import { ResolverTag } from '@ps/web-core-enums';
import type { LabelProp } from '@ps/web-shared-components';
import { Link } from '@ps/web-shared-components';
import type { ReactNode } from 'react';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import { Country, EulaType } from '@swf/core/enums';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import { select } from '@swf/core/store';

import type { Props as RegulatoryCheckboxesProps } from '../RegulatoryCheckboxes';

import {
  TXTCLI_By_checking_this_box_I_confirm_ELL,
  TXTCLI_I_acknowledge_that_I_am_prohib_ELL,
  TXTCLI_I_confirm_that_I_am_at_least_N_ELL,
  TXTCLI_I_confirm_that_I_am_at_least_N_ELL_1,
  TXTCLI_I_consent_to_receive_details_a_ELL,
  TXTCLI_I_have_read_and_understood_the_ELL_6,
  TXTCLI_I_have_read_and_understood_the_ELL_7,
  TXTCLI_I_have_read_and_understood_the_ELL_8,
  TXTCLI_I_understand_that_I_can_always_ELL,
  TXTCLI_The_information_I_have_provide_ELL,
  TXTCLI_The_information_I_provided_is__ELL,
  TXTMOB_I_consent_to_receive_details_o_ELL,
  TXTWCL_I_agree_that_I_am_not_currentl_ELL_2,
  TXTWCL_I_am_Xa_hrefXXN0XXeligibleX_aX_ELL_1,
  TXTWCL_I_confirm_that_I_am_acting_on__ELL,
  TXTWCL_I_confirm_that_I_am_at_least_N_ELL,
  TXTWCL_Underage_gambling_is_an_offenc_ELL,
  TXTWCL_We_do_not_offer_real_money_pla_ELL
} from './useLabel.messages.json';

enum AREulaState {
  BUENOS_AIRES = 'B',
  CABA = 'C'
}

type Props = RegulatoryCheckboxesProps & { eulaType: EulaType };

const isAREulaState = (country?: string, state?: string) =>
  country === Country.Argentina && Object.values(AREulaState).includes(state as AREulaState);

const trackableLink = (msg: ReactNode, onClick?: () => void) => (
  <Link
    onClick={(e: React.MouseEvent) => {
      e.preventDefault();
      onClick?.();
    }}
  >
    {msg}
  </Link>
);

const resolverLink = (msg: string, resolverTag: ResolverTag) => (
  <ResolverLink resolverTag={resolverTag}>{msg}</ResolverLink>
);

const eulaLabelMapper = (siteId: Sites) => {
  switch (siteId) {
    case Sites.ROM:
    case Sites.USMI:
    case Sites.US_NJ:
    case Sites.US_PA:
      return TXTCLI_I_confirm_that_I_am_at_least_N_ELL_1;
    default:
      return TXTWCL_I_confirm_that_I_am_at_least_N_ELL;
  }
};

const useLabel = (
  onEulaLinkClick: Props['onEulaLinkClick'],
  onPrivacyLinkClick: Props['onPrivacyLinkClick']
) => {
  const siteId = useSelector(select.cms.getSiteId);
  const legalAge = useSelector(select.cms.getLegalAge);
  const searchParams = useSearchParams<{ country: string; state: string }>();
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);

  const getEulaResolverTag = useCallback(() => {
    const { country, state } = searchParams || {};
    if (siteId === Sites.CA_ON) return ResolverTag.EligibleEula;
    return isAREulaState(country, state) ? ResolverTag.MCEulaARStates : ResolverTag.MCEula;
  }, [searchParams, siteId]);

  return useCallback(
    (variant: Props['variant'], eulaType: EulaType) => {
      const eulaResolverTag = getEulaResolverTag();

      const createLink = (
        msg: ReactNode,
        onClick: Props['onEulaLinkClick'] | Props['onPrivacyLinkClick'],
        isMobile: boolean,
        resolverTag: ResolverTag
      ): ReactNode =>
        isMobile ? resolverLink(msg as string, resolverTag) : trackableLink(msg, onClick);

      const eulaLink = (msg: ReactNode): ReactNode =>
        createLink(msg, onEulaLinkClick, isMobileEmbedded, eulaResolverTag);

      const privacyLink = (msg: ReactNode): ReactNode =>
        createLink(msg, onPrivacyLinkClick, isMobileEmbedded, ResolverTag.MCPrivacy);

      const labels: Record<Props['variant'] | 'default', { [key in EulaType]?: LabelProp }> = {
        first: {
          [EulaType.Eula]: [
            {
              ...TXTCLI_I_confirm_that_I_am_at_least_N_ELL,
              values: {
                0: legalAge,
                a: (msg: ReactNode) => eulaLink(msg)
              }
            }
          ],
          [EulaType.Privacy]: [
            {
              ...TXTCLI_I_have_read_and_understood_the_ELL_6,
              values: {
                a: (msg: ReactNode) => privacyLink(msg)
              }
            }
          ],
          [EulaType.CombinedEulaPrivacy]: [
            {
              ...eulaLabelMapper(siteId),
              values: {
                0: legalAge,
                a: (msg: ReactNode) => eulaLink(msg),
                a2: (msg: ReactNode) => privacyLink(msg)
              }
            }
          ],
          [EulaType.IAmEligibleToPlay]: [
            {
              tagName: 'span',
              ...TXTWCL_I_am_Xa_hrefXXN0XXeligibleX_aX_ELL_1,
              values: {
                a: (msg: ReactNode) => eulaLink(msg)
              }
            }
          ]
        },
        rmf: {
          [EulaType.PlayerIsNot3Party]: [
            [Sites.USMI, Sites.US_NJ, Sites.US_PA].includes(siteId)
              ? TXTCLI_The_information_I_provided_is__ELL
              : TXTWCL_I_confirm_that_I_am_acting_on__ELL
          ],
          [EulaType.InformationIsComplete]: [
            [Sites.USMI, Sites.US_NJ, Sites.US_PA].includes(siteId)
              ? TXTCLI_I_acknowledge_that_I_am_prohib_ELL
              : TXTCLI_The_information_I_have_provide_ELL
          ],
          [EulaType.PortugalEligibleToPlay]: [TXTWCL_I_agree_that_I_am_not_currentl_ELL_2]
        },
        stars: {
          [EulaType.Eula]:
            siteId === Sites.UK
              ? [
                  { tagName: 'b', ...TXTWCL_Underage_gambling_is_an_offenc_ELL },
                  {
                    ...TXTCLI_I_confirm_that_I_am_at_least_N_ELL,
                    values: {
                      0: legalAge,
                      a: (msg: React.ReactNode) => resolverLink(msg as string, eulaResolverTag)
                    }
                  }
                ]
              : [
                  {
                    ...TXTCLI_I_confirm_that_I_am_at_least_N_ELL,
                    values: {
                      0: legalAge,
                      a: (msg: React.ReactNode) => resolverLink(msg as string, eulaResolverTag)
                    }
                  }
                ],
          [EulaType.Privacy]: [
            {
              ...TXTCLI_I_have_read_and_understood_the_ELL_6,
              values: {
                a: (msg: React.ReactNode) => resolverLink(msg as string, ResolverTag.MCPrivacy)
              }
            }
          ],
          [EulaType.CombinedEulaPrivacy]: [
            {
              tagName: 'p',
              ...TXTCLI_I_confirm_that_I_am_at_least_N_ELL_1,
              values: {
                0: legalAge,
                a: (msg: React.ReactNode) => resolverLink(msg as string, eulaResolverTag),
                a2: (msg: React.ReactNode) => resolverLink(msg as string, ResolverTag.MCPrivacy)
              }
            }
          ],
          [EulaType.MarketingMailList]:
            siteId === Sites.ITA
              ? [
                  TXTCLI_I_consent_to_receive_details_a_ELL,
                  TXTCLI_I_understand_that_I_can_always_ELL
                ]
              : [TXTMOB_I_consent_to_receive_details_o_ELL]
        },
        default: {
          [EulaType.GamingAccAgreement]: [
            {
              tagName: 'p',
              ...TXTCLI_I_have_read_and_understood_the_ELL_7,
              values: {
                a: (msg: React.ReactNode) =>
                  resolverLink(msg as string, ResolverTag.GamingAccountAgreement)
              }
            }
          ],
          [EulaType.GameRulesConditions]: [
            { tagName: 'p', ...TXTCLI_I_have_read_and_understood_the_ELL_8 }
          ],
          [EulaType.IndianStatesRestrictions]: [
            TXTWCL_We_do_not_offer_real_money_pla_ELL,
            TXTCLI_By_checking_this_box_I_confirm_ELL
          ]
        }
      };

      return labels[variant][eulaType] || labels.default[eulaType];
    },
    [legalAge, siteId, isMobileEmbedded, getEulaResolverTag, onEulaLinkClick, onPrivacyLinkClick]
  );
};

export default useLabel;
